import { useState, useEffect, useRef, useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { toast } from "sonner";
import { api } from "../lib/api";

// Node colors by type
const NODE_COLORS = {
  client: "#3b82f6",      // Blue
  supplier: "#10b981",    // Green
  employee: "#8b5cf6",    // Purple
  other: "#f59e0b",       // Amber (KYU)
  person: "#6b7280",      // Gray
  family: "#ec4899",      // Pink
  external_company: "#f97316" // Orange
};

// KYU category colors
const KYU_COLORS = {
  competitor: "#ef4444",  // Red
  influencer: "#a855f7",  // Purple
  ipss: "#22c55e",        // Green
  other: "#f59e0b"        // Amber
};

// Link colors by type
const LINK_COLORS = {
  conflict: "#ef4444",    // Red
  beneficial_owner: "#3b82f6",
  shareholder: "#10b981",
  family: "#ec4899",
  external_position: "#f97316"
};

export const RelationshipGraphPage = () => {
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  const [highlightNodes, setHighlightNodes] = useState(new Set());
  const [highlightLinks, setHighlightLinks] = useState(new Set());
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const graphRef = useRef();

  const fetchGraphData = useCallback(async () => {
    try {
      const response = await api.get('/graph/relationships');
      setGraphData({
        nodes: response.data.nodes || [],
        links: response.data.links || []
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching graph data:', error);
      toast.error('Erro ao carregar dados do grafo');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  const handleNodeClick = useCallback((node) => {
    setSelectedNode(node);
    
    // Highlight connected nodes and links
    const connectedNodes = new Set();
    const connectedLinks = new Set();
    
    graphData.links.forEach(link => {
      if (link.source.id === node.id || link.target.id === node.id) {
        connectedNodes.add(link.source.id);
        connectedNodes.add(link.target.id);
        connectedLinks.add(link);
      }
    });
    
    setHighlightNodes(connectedNodes);
    setHighlightLinks(connectedLinks);
  }, [graphData]);

  const clearSelection = () => {
    setSelectedNode(null);
    setHighlightNodes(new Set());
    setHighlightLinks(new Set());
  };

  const getNodeColor = (node) => {
    if (node.type === 'other') {
      return KYU_COLORS[node.category] || KYU_COLORS.other;
    }
    return NODE_COLORS[node.type] || "#6b7280";
  };

  const getNodeSize = (node) => {
    if (highlightNodes.size > 0 && !highlightNodes.has(node.id)) {
      return 4;
    }
    if (node.type === 'person' || node.type === 'family') return 6;
    if (node.type === 'external_company') return 7;
    return 10;
  };

  const getLinkColor = (link) => {
    if (link.type === 'conflict') {
      if (link.severity === 'critical') return '#dc2626';
      if (link.severity === 'high') return '#ef4444';
      return '#f97316';
    }
    return LINK_COLORS[link.type] || "#9ca3af";
  };

  const getLinkWidth = (link) => {
    if (highlightLinks.size > 0 && !highlightLinks.has(link)) {
      return 0.5;
    }
    if (link.type === 'conflict') {
      if (link.severity === 'critical') return 4;
      if (link.severity === 'high') return 3;
      return 2;
    }
    return 1;
  };

  // Filter data based on selections
  const filteredData = useCallback(() => {
    let nodes = [...graphData.nodes];
    let links = [...graphData.links];

    // Filter by type
    if (filterType !== 'all') {
      const typeMapping = {
        'clients': ['client'],
        'suppliers': ['supplier'],
        'employees': ['employee'],
        'kyu': ['other'],
        'people': ['person', 'family']
      };
      const allowedTypes = typeMapping[filterType] || [];
      nodes = nodes.filter(n => allowedTypes.includes(n.type));
      const nodeIds = new Set(nodes.map(n => n.id));
      links = links.filter(l => nodeIds.has(l.source.id || l.source) && nodeIds.has(l.target.id || l.target));
    }

    // Filter conflicts only
    if (showConflictsOnly) {
      const conflictLinks = links.filter(l => l.type === 'conflict');
      const conflictNodeIds = new Set();
      conflictLinks.forEach(l => {
        conflictNodeIds.add(l.source.id || l.source);
        conflictNodeIds.add(l.target.id || l.target);
      });
      nodes = nodes.filter(n => conflictNodeIds.has(n.id));
      links = conflictLinks;
    }

    return { nodes, links };
  }, [graphData, filterType, showConflictsOnly]);

  const handleScanConflicts = async () => {
    try {
      toast.info('Análise de conflitos iniciada...', { duration: 2000 });
      const response = await api.post('/conflicts/scan');
      toast.success(`Análise iniciada: ${response.data.entities_scanned} entidades`);
      setTimeout(() => {
        fetchGraphData();
      }, 6000);
    } catch (error) {
      console.error('Error scanning conflicts:', error);
      toast.error('Erro ao iniciar análise');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filtered = filteredData();

  return (
    <div data-testid="relationship-graph-page" className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Grafo de Relacionamentos</h1>
          <p className="text-muted-foreground">Visualização de conexões e conflitos entre entidades</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleScanConflicts}
            className="px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors flex items-center gap-2"
            data-testid="scan-conflicts-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Analisar Conflitos
          </button>
          <button
            onClick={fetchGraphData}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.client }}></div>
              <span className="text-sm text-muted-foreground">Clientes</span>
            </div>
            <p className="text-2xl font-bold">{stats.clients}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.supplier }}></div>
              <span className="text-sm text-muted-foreground">Fornecedores</span>
            </div>
            <p className="text-2xl font-bold">{stats.suppliers}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.employee }}></div>
              <span className="text-sm text-muted-foreground">Colaboradores</span>
            </div>
            <p className="text-2xl font-bold">{stats.employees}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.other }}></div>
              <span className="text-sm text-muted-foreground">KYU</span>
            </div>
            <p className="text-2xl font-bold">{stats.others}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <span className="text-sm text-muted-foreground">Total Nós</span>
            </div>
            <p className="text-2xl font-bold">{stats.total_nodes}</p>
          </div>
          <div className="p-4 rounded-xl border bg-card border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-muted-foreground">Conflitos</span>
            </div>
            <p className="text-2xl font-bold text-red-500">{stats.conflicts}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl border bg-card">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Filtrar por tipo:</label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-input bg-background text-sm"
          >
            <option value="all">Todos</option>
            <option value="clients">Clientes (KYC)</option>
            <option value="suppliers">Fornecedores (KYS)</option>
            <option value="employees">Colaboradores (KYE)</option>
            <option value="kyu">Outros (KYU)</option>
            <option value="people">Pessoas Relacionadas</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="conflictsOnly"
            checked={showConflictsOnly}
            onChange={(e) => setShowConflictsOnly(e.target.checked)}
            className="rounded border-input"
          />
          <label htmlFor="conflictsOnly" className="text-sm font-medium">Mostrar apenas conflitos</label>
        </div>
        {selectedNode && (
          <button
            onClick={clearSelection}
            className="px-3 py-1.5 rounded-lg bg-muted text-sm font-medium hover:bg-muted/80 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Limpar seleção
          </button>
        )}
      </div>

      {/* Graph and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Graph */}
        <div className="lg:col-span-3 rounded-xl border bg-card overflow-hidden" style={{ height: '600px' }}>
          {filtered.nodes.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Nenhuma entidade encontrada
            </div>
          ) : (
            <ForceGraph2D
              ref={graphRef}
              graphData={filtered}
              nodeLabel={(node) => `${node.name} (${node.type})`}
              nodeColor={getNodeColor}
              nodeVal={getNodeSize}
              linkColor={getLinkColor}
              linkWidth={getLinkWidth}
              linkDirectionalParticles={link => link.type === 'conflict' ? 4 : 0}
              linkDirectionalParticleWidth={3}
              linkDirectionalParticleColor={getLinkColor}
              onNodeClick={handleNodeClick}
              onBackgroundClick={clearSelection}
              cooldownTicks={100}
              nodeCanvasObjectMode={() => 'after'}
              nodeCanvasObject={(node, ctx, globalScale) => {
                const label = node.name;
                const fontSize = 12 / globalScale;
                ctx.font = `${fontSize}px Sans-Serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = highlightNodes.size > 0 && !highlightNodes.has(node.id) ? '#9ca3af' : '#ffffff';
                ctx.fillText(label, node.x, node.y + 15);
              }}
            />
          )}
        </div>

        {/* Legend and Details */}
        <div className="space-y-4">
          {/* Legend */}
          <div className="rounded-xl border bg-card p-4">
            <h3 className="font-semibold mb-3">Legenda</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.client }}></div>
                <span>Cliente (KYC)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.supplier }}></div>
                <span>Fornecedor (KYS)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.employee }}></div>
                <span>Colaborador (KYE)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: KYU_COLORS.competitor }}></div>
                <span>KYU - Concorrente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: KYU_COLORS.ipss }}></div>
                <span>KYU - IPSS</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.person }}></div>
                <span>Pessoa Relacionada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: NODE_COLORS.family }}></div>
                <span>Familiar</span>
              </div>
              <hr className="my-2" />
              <div className="flex items-center gap-2">
                <div className="w-6 h-1 rounded" style={{ backgroundColor: LINK_COLORS.conflict }}></div>
                <span>Conflito</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 rounded" style={{ backgroundColor: LINK_COLORS.shareholder }}></div>
                <span>Acionista</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 rounded" style={{ backgroundColor: LINK_COLORS.family }}></div>
                <span>Familiar</span>
              </div>
            </div>
          </div>

          {/* Selected Node Details */}
          {selectedNode && (
            <div className="rounded-xl border bg-card p-4">
              <h3 className="font-semibold mb-3">Detalhes</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Nome:</span>
                  <p className="font-medium">{selectedNode.name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Tipo:</span>
                  <p className="font-medium capitalize">{selectedNode.type}</p>
                </div>
                {selectedNode.tax_id && (
                  <div>
                    <span className="text-muted-foreground">NIF:</span>
                    <p className="font-medium">{selectedNode.tax_id}</p>
                  </div>
                )}
                {selectedNode.category && (
                  <div>
                    <span className="text-muted-foreground">Categoria:</span>
                    <p className="font-medium capitalize">{selectedNode.category}</p>
                  </div>
                )}
                {selectedNode.risk_level && (
                  <div>
                    <span className="text-muted-foreground">Risco:</span>
                    <p className={`font-medium ${selectedNode.risk_level === 'high' ? 'text-red-500' : selectedNode.risk_level === 'medium' ? 'text-amber-500' : 'text-green-500'}`}>
                      {selectedNode.risk_level === 'high' ? 'Alto' : selectedNode.risk_level === 'medium' ? 'Médio' : 'Baixo'}
                    </p>
                  </div>
                )}
                <div className="pt-2">
                  <span className="text-muted-foreground">Conexões:</span>
                  <p className="font-medium">{highlightNodes.size - 1} entidades conectadas</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelationshipGraphPage;
